import { type JSX,SyntheticEvent, useEffect, useState } from 'react';
import { Outlet, useLocation,useParams } from 'react-router-dom';

import { CircularProgress, Tabs,Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { NotFound } from 'common/components/NotFound';
import { Page } from 'common/components/Page';
import { selectPageInfo, setPageInfo } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { ResourceDetailHeader } from 'common/components/ResourceDetailHeader';
import { UtmStatusBadge } from 'common/components/sharedCustomComponents/StatusBadge/UtmStatusBadge';
import { StyledBreadcrumbs } from 'common/components/StyledBreadcrumbs';
import { StyledTab } from 'common/components/StyledTab';
import { Routes, RoutesNameMap } from 'common/constants/routes';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchTenantUserMembershipDetails } from 'common/services/api/TenantUserMembership/TenantUserMembership.thunk';
import { FetchTenantUserMembershipDetailsResponse } from 'common/services/api/TenantUserMembership/TenantUserMembershipThunkTypeDefs';
import { getLastPathSegment } from 'common/utils/locationHelpers';

import { UserGlobalRoles } from './UserGlobalRoles';

const USER_ID_SECTION_INLINE_STYLES = {
    justifyContent: 'left',
    marginTop: '0.5rem',
    display: 'flex',
    alignItems: 'center'
};

const OASYS_ACCOUNT_SECTION_INLINE_STYLES = {
    justifyContent: 'left',
    display: 'flex',
    alignItems: 'center'
};

export function TenantUserMembershipDetails(): JSX.Element {
    const [currentTab, setCurrentTab] = useState<Routes>(Routes.Roles);
    const [processing, setProcessing] = useState<boolean>(true);
    const [error, setError] = useState<string>('');

    const location = useLocation();
    const params = useParams();

    useEffect(() => {
        const lastPathSegment = getLastPathSegment();
        setCurrentTab(lastPathSegment as Routes);
    }, [location]);

    const dispatch = useAppDispatch();
    useEffect(() => {
        const fetchParams = {
            appSuiteId: params?.suiteId ?? '',
            tenantId: params?.tenantId ?? '',
            userId: params?.userId ?? '',
        };
        dispatch(fetchTenantUserMembershipDetails(fetchParams))
            .unwrap()
            .then((result) => {
                dispatch(setPageInfo(result));
            })
            .catch((err) => {
                setError(err.message);
            })
            .finally(() => {
                setProcessing(false);
            });
    }, []);

    const getTabLink = (key: string) => `/${Routes.ApplicationSuites}/${params.suiteId}/${Routes.Tenants}/${params.tenantId}/${Routes.Users}/${params.userId}/${key}`;
    const handleChangeTab = (_event: SyntheticEvent, newValue: Routes) => {
        setCurrentTab(newValue);
    };

    const userDetails = useAppSelector(selectPageInfo) as FetchTenantUserMembershipDetailsResponse;
    const errorCode = extractStatusCode(error);

    if (errorCode === 404) {
        return <NotFound />;
    }

    if (errorCode && errorCode !== 200) {
        return <p>Something went wrong</p>;
    }

    if(processing) {
        return <CircularProgress color='primary'/>;
    }

    const globalRoles = userDetails?.globalRoles?.split(', ') ?? [];
    const getUserFullName = (userDetailsResponse: FetchTenantUserMembershipDetailsResponse): string => {
        const UNKNOWN_USER_DEFAULT_FULL_NAME = 'Unknown User';
        const { firstName='', lastName='' } = userDetailsResponse ?? {};
        const existingUserFullName = `${firstName} ${lastName}`;
        return firstName && lastName ? existingUserFullName : UNKNOWN_USER_DEFAULT_FULL_NAME;
    };
    const buildPageTitle = (): string => {
        const userFullName = getUserFullName(userDetails);
        return `${userFullName} - ${RoutesNameMap.get(Routes.User)}`;
    };

    return (
        <Page
            title={buildPageTitle()}
            header={(
                <Typography variant="h5" gutterBottom component="div">
                    {userDetails?.id &&
                        <StyledBreadcrumbs
                            guidNameMappings={[{
                                resourceId: params.suiteId ?? '',
                                resourceName: userDetails?.applicationSuiteName || ''
                            }, {
                                resourceId: params.tenantId ?? '',
                                resourceName: userDetails?.name || ''
                            }, {
                                resourceId: params.userId ?? '',
                                resourceName: getUserFullName(userDetails)
                            }]}
                        /> }
                    <>
                        <Typography
                            variant='h5'
                            sx={{ display: 'inline', marginRight: '1rem' }}
                        >
                            {getUserFullName(userDetails)}
                        </Typography>
                        <UtmStatusBadge
                            tooltipProps={userDetails}
                            status={userDetails?.status ?? ''}
                            chipSize='small'
                        />
                    </>
                    <div style={{ display: 'flex' }}>
                        <div>
                            <ResourceDetailHeader
                                resourceType='User'
                                resourceId={params.userId ?? ''}
                                shouldShowResourceName={false}
                                resourceTypeInlineStyles={USER_ID_SECTION_INLINE_STYLES}
                            />
                            <ResourceDetailHeader
                                resourceType='Oasys Account'
                                resourceId={userDetails?.oasysId ?? ''}
                                shouldShowResourceName={false}
                                resourceTypeInlineStyles={OASYS_ACCOUNT_SECTION_INLINE_STYLES}
                                useDefaultResourceTypeTitle={false}
                            />
                        </div>
                        <UserGlobalRoles
                            title='Global Roles:'
                            globalRoles={globalRoles}
                        />
                    </div>
                </Typography>
            )}
        >
            <Tabs
                sx={{ mb: '-60px', zIndex:1200, width: '40rem'  }}
                onChange={handleChangeTab}
                value={currentTab}
            >
                <StyledTab
                    disabled={false}
                    label={RoutesNameMap.get(Routes.Roles)}
                    key={Routes.Roles}
                    link={getTabLink(Routes.Roles)}
                    value={Routes.Roles}
                />
                <StyledTab
                    disabled={false}
                    label={RoutesNameMap.get(Routes.UserGroups)}
                    key={Routes.UserGroups}
                    link={getTabLink(Routes.UserGroups)}
                    value={Routes.UserGroups}
                />
                <StyledTab
                    disabled={false}
                    label={RoutesNameMap.get(Routes.Claims)}
                    key={Routes.Claims}
                    link={getTabLink(Routes.Claims)}
                    value={Routes.Claims}
                />
                <StyledTab
                    disabled={false}
                    label={RoutesNameMap.get(Routes.AclEntries)}
                    key={Routes.AclEntries}
                    link={getTabLink(Routes.AclEntries)}
                    value={Routes.AclEntries}
                />
            </Tabs>
            <Outlet />
        </Page>
    );
}
